/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { toast } from 'react-toastify';
import { TableUtility } from '@utility/dynamic-table';
import lottie from 'lottie-web';
import PropTypes from 'prop-types';
import { useAuth } from '../../../Hooks/useAuth';
import UseAxios from '../../../Api/http/http';
import emptyStateAnimationData from '../../../Assets/images/json/dar_no_data.json';
import PuzzleLoader from '../../../Assets/images/svg/loading puzzle.gif';
import { PROJECT_COST_REPORT } from '../../../Api/resources/endPoint';
import { serviceName } from '../../../Api/resources/serviceConstants';
import './ProjectCostReport.scss';
import { buildProjectCostApiUrl } from '../../../Api/request/projectCostReport';
import {
  getExportFileName,
} from '../../Delivery/utils';
// ─── Helpers ────────────────────────────────────────────────────────────────

const getStoredAuthToken = () => localStorage.getItem('authToken') || '';

const projectCostReportBaseUrl = buildProjectCostApiUrl(PROJECT_COST_REPORT);

// ─── Export Transform ────────────────────────────────────────────────────────
// Clean the export payload the same way other modules do: strip JSX from column
// headers, remove rowConfiguration from data rows, and filter out hidden/filter
// columns so Excel only shows real data columns.
const PLAIN_COLUMN_HEADERS = {
  projectExpense: 'Project Expense (INR)',
};

const normalizeManagerNames = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const normalized = raw
    .replace(/\r?\n+/g, ',')
    .replace(/\s*[|;]+\s*/g, ',')
    .replace(/\s*,\s*/g, ',')
    .replace(/,+/g, ',');

  const uniqueNames = Array.from(new Set(
    normalized
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean),
  ));

  return uniqueNames.join(', ');
};

const getManagerSummary = (rowData = {}) => {
  const direct = normalizeManagerNames(rowData.deliveryManagerSrManager);
  if (direct) return direct;

  const combined = [
    normalizeManagerNames(rowData.deliveryManager),
    normalizeManagerNames(rowData.srManagerProjectManager),
  ]
    .filter(Boolean)
    .join(', ');

  return normalizeManagerNames(combined);
};

const PROJECT_COST_COLUMNS = [
  ['projectId', 'Project ID', 'string'],
  ['projectName', 'Project Name', 'string'],
  ['clientName', 'Client Name', 'string'],
  ['customerType', 'Customer Type', 'string'],
  ['projectRegion', 'Project Region', 'string'],
  ['projectCountry', 'Project Country', 'string'],
  ['projectVertical', 'Project Vertical', 'string'],
  ['projectType', 'Project Type', 'string'],
  ['projectStatus', 'Project Status', 'string'],
  ['billingEntity', 'Billing Entity', 'string'],
  ['deliveryManagerSrManager', 'Delivery Manager / Sr. Manager / Project Manager', 'string'],
  ['salesSpoc', 'Sales SPOC', 'string'],
  ['actualStartDate', 'Actual Start Date', 'date'],
  ['replannedEndDate', 'Replanned End Date', 'date'],
  ['invoiceAmount', 'Invoice Amount (INR)', 'number'],
  ['zohoClientId', 'Zoho Client ID', 'string'],
  ['zohoClientName', 'Zoho Client Name', 'string'],
  ['employeeId', 'Employee ID', 'string'],
  ['employeeName', 'Employee Name', 'string'],
  ['loggedEffort', 'Logged Effort (hrs)', 'number'],
  ['unloggedEffort', 'Unlogged Effort (hrs)', 'number'],
  ['totalEffort', 'Total Effort (hrs)', 'number'],
  ['expectedEffort', 'Expected Effort (hrs)', 'number'],
  ['expectedDistribution', 'Effort Distribution (%)', 'number'],
  ['salaryDistribution', 'Salary Distribution (INR)', 'number'],
  ['travelAndAccommodationCost', 'Travel & Accommodation Cost (INR)', 'number'],
  ['otherCost', 'Other Cost (INR)', 'number'],
  ['consultantCost', 'Consultant Cost (INR)', 'number'],
  ['softwareHardwareCost', 'Software/Hardware Cost (INR)', 'number'],
  ['projectExpense', 'Project Expense (INR)', 'number'],
  ['overallTotalCost', 'Overall Total Cost (INR)', 'number'],
];

// These project-level cost/invoice fields repeat the same value on every
// employee row of a project (a project can have multiple Sr. Managers /
// Client Names, each producing its own row). The per-project "<Project> -
// Total" row already consolidates them correctly, so the individual employee
// rows must not display these duplicated values — they show as 0 instead.
const ZERO_DISPLAY_COST_FIELDS = [
  'invoiceAmount', 'travelAndAccommodationCost', 'otherCost',
  'consultantCost', 'softwareHardwareCost', 'projectExpense', 'overallTotalCost',
];

const transformProjectCostExport = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;

  const rawTableConfig = Array.isArray(payload?.tableConfiguration)
    ? payload.tableConfiguration
    : (Array.isArray(payload?.data?.tableConfiguration) ? payload.data.tableConfiguration : []);
  const rawTableData = Array.isArray(payload?.tableData)
    ? payload.tableData
    : (Array.isArray(payload?.data?.tableData) ? payload.data.tableData : []);

  const sourceColumns = new Map(rawTableConfig.map((column) => [column.field, column]));
  const cleanTableConfig = PROJECT_COST_COLUMNS.map(([field, defaultHeader, type], index) => {
    const sourceCol = sourceColumns.get(field) || {};
    const header = PLAIN_COLUMN_HEADERS[field]
      || (typeof sourceCol.header === 'string' ? sourceCol.header : defaultHeader);

    return {
      ...sourceCol,
      field,
      header,
      type: type || 'string',
      numeric: type === 'number',
      visible: true,
      order: index + 1,
      ...(type === 'number'
        ? {
          align: 'right',
          headerAlign: 'right',
          exportAlign: 'right',
          alignment: { horizontal: 'right' },
          headerAlignment: { horizontal: 'right' },
          exportAlignment: { horizontal: 'right' },
        }
        : {}),
    };
  });

  const cleanTableData = rawTableData.map((row) => {
    const {
      rowConfiguration: __rc,
      _isFooterRow: __fr,
      _isProjectTotalRow: __ptr,
      ...rest
    } = row || {};
    const exportRow = {};

    const isProjectTotalRow = Boolean(
      row?._isProjectTotalRow
      || (typeof row?.projectName === 'string' && row.projectName.endsWith(' - Total')),
    );
    const isFooterRow = Boolean(
      row?._isFooterRow
      || row?.projectId === 'Total'
      || row?.projectName === 'Total',
    );
    const isEmployeeRow = !isProjectTotalRow && !isFooterRow;

    if (!rest.deliveryManagerSrManager && (rest.deliveryManager || rest.srManagerProjectManager)) {
      rest.deliveryManagerSrManager = getManagerSummary(rest);
    }

    PROJECT_COST_COLUMNS.forEach(([field, , type]) => {
      if (isEmployeeRow && ZERO_DISPLAY_COST_FIELDS.includes(field)) {
        exportRow[field] = 0;
        return;
      }
      const value = rest[field];
      if (type === 'number') {
        if (value !== undefined && value !== null && value !== '' && value !== '-') {
          const num = typeof value === 'string' ? Number(value.replace(/,/g, '')) : Number(value);
          exportRow[field] = Number.isFinite(num) ? parseFloat(num.toFixed(2)) : null;
          return;
        }
        exportRow[field] = null;
        return;
      }
      exportRow[field] = (value !== undefined && value !== null && value !== '') ? value : null;
    });

    return exportRow;
  });

  return {
    ...payload,
    reportTitle: 'Project Cost Report',
    title: 'Project Cost Report',
    tableConfiguration: cleanTableConfig,
    tableData: cleanTableData,
    ...(payload?.data
      ? {
        data: {
          ...payload.data,
          reportTitle: 'Project Cost Report',
          title: 'Project Cost Report',
          tableConfiguration: cleanTableConfig,
          tableData: cleanTableData,
        },
      }
      : {}),
  };
};

const normalizeMonthYearPart = (yearPart) => {
  const normalizedYear = String(yearPart || '').trim();
  if (!normalizedYear) return null;
  if (normalizedYear.length === 2) return `20${normalizedYear}`;
  return /^\d{4}$/.test(normalizedYear) ? normalizedYear : null;
};

const toApiMonthRangeValue = (value, { useEndOfMonth = false } = {}) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = useEndOfMonth ? new Date(year, month, 0).getDate() : 1;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const normalized = String(value).trim();
  if (!normalized) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    const lastDay = new Date(Number(year), Number(month), 0).getDate();
    return `${year}-${String(Number(month)).padStart(2, '0')}-${String(useEndOfMonth ? lastDay : 1).padStart(2, '0')}`;
  }

  const numericMonthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{2}|\d{4})$/);
  if (numericMonthYearMatch) {
    const [, monthPart, yearPart] = numericMonthYearMatch;
    const year = normalizeMonthYearPart(yearPart);
    const month = Number(monthPart);
    if (!year || Number.isNaN(month) || month < 1 || month > 12) return null;
    const lastDay = new Date(Number(year), month, 0).getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(useEndOfMonth ? lastDay : 1).padStart(2, '0')}`;
  }

  const monthNameYearMatch = normalized.match(/^([A-Za-z]{3,9})[-/\s](\d{2}|\d{4})$/);
  if (monthNameYearMatch) {
    const [, monthName, yearPart] = monthNameYearMatch;
    const year = normalizeMonthYearPart(yearPart);
    if (!year) return null;
    const parsed = new Date(`${monthName} 1, ${year}`);
    if (Number.isNaN(parsed.getTime())) return null;
    return toApiMonthRangeValue(parsed, { useEndOfMonth });
  }

  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return toApiMonthRangeValue(parsedDate, { useEndOfMonth });
};


// Mirrors the legacy emp_salary_project_expense_report.php formatting:
// effort columns use number_format($val, 2, '.', ',') (Western grouping),
// money columns use indian_number_format() (Indian lakh/crore grouping).
const WESTERN_COMMA_FIELDS = new Set([
  'loggedEffort', 'unloggedEffort', 'totalEffort', 'expectedEffort',
]);

const INDIAN_COMMA_FIELDS = new Set([
  'invoiceAmount', 'salaryDistribution', 'travelAndAccommodationCost', 'otherCost',
  'consultantCost', 'softwareHardwareCost', 'projectExpense', 'overallTotalCost',
]);


const formatWesternNumber = (value) => {
  const num = Number(value);
  const safeNum = Number.isFinite(num) ? num : 0;
  return safeNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatIndianCurrency = (value) => {
  const num = Number(value);
  const safeNum = Number.isFinite(num) ? num : 0;
  const isNegative = safeNum < 0;
  const [intPart, decPart] = Math.abs(safeNum)
    .toFixed(2)
    .split('.');

  let formattedInt = intPart;
  if (intPart.length > 3) {
    const lastThree = intPart.slice(-3);
    const rest = intPart.slice(0, -3);
    const restWithCommas = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    formattedInt = `${restWithCommas},${lastThree}`;
  }

  return `${isNegative ? '-' : ''}${formattedInt}.${decPart}`;
};

const toSafeNumber = (rawValue) => {
  const num = Number(rawValue);
  return Number.isFinite(num) ? parseFloat(num.toFixed(2)) : 0;
};

// Mirrors emp_salary_project_expense_report.php's per-project subtotal row:
// effort + salary columns are summed across the project's employee rows,
// while project-level cost/invoice columns are taken once per project
// (they already repeat on every employee row for that project).
const SUMMED_NUMERIC_FIELDS = [
  'loggedEffort', 'unloggedEffort', 'totalEffort', 'expectedEffort', 'salaryDistribution',
];
const TAKE_ONCE_NUMERIC_FIELDS = [
  'invoiceAmount', 'travelAndAccommodationCost', 'otherCost', 'consultantCost',
  'softwareHardwareCost', 'projectExpense', 'overallTotalCost',
];
const BOLD_NUMERIC_FIELDS = [...SUMMED_NUMERIC_FIELDS, ...TAKE_ONCE_NUMERIC_FIELDS];

const isPresentValue = (value) => value !== undefined && value !== null && value !== '';

// Numeric columns show a bold "-" only when the underlying total could not be
// computed (no row ever carried a value for that field); a genuine 0 total
// still prints as a formatted number.
const formatNumericTotalField = (hasValue, total, formatter) =>
  (hasValue ? formatter(total) : '-');

const buildProjectTotalRow = (projectTotals, projectLabel) => ({
  projectName: projectLabel,
  clientName: '',
  projectRegion: '',
  projectCountry: '',
  projectVertical: '',
  projectType: '',
  projectStatus: '',
  billingEntity: '',
  deliveryManagerSrManager: '',
  salesSpoc: '',
  actualStartDate: '',
  replannedEndDate: '',
  invoiceAmount: formatNumericTotalField(projectTotals.invoiceAmountHasValue, projectTotals.invoiceAmount, formatIndianCurrency),
  zohoClientId: '',
  zohoClientName: '',
  employeeId: '',
  employeeName: '',
  loggedEffort: formatNumericTotalField(projectTotals.loggedEffortHasValue, projectTotals.loggedEffort, formatWesternNumber),
  unloggedEffort: formatNumericTotalField(projectTotals.unloggedEffortHasValue, projectTotals.unloggedEffort, formatWesternNumber),
  totalEffort: formatNumericTotalField(projectTotals.totalEffortHasValue, projectTotals.totalEffort, formatWesternNumber),
  expectedEffort: formatNumericTotalField(projectTotals.expectedEffortHasValue, projectTotals.expectedEffort, formatWesternNumber),
  expectedDistribution: '-',
  salaryDistribution: formatNumericTotalField(projectTotals.salaryDistributionHasValue, projectTotals.salaryDistribution, formatIndianCurrency),
  travelAndAccommodationCost: formatNumericTotalField(projectTotals.travelAndAccommodationCostHasValue, projectTotals.travelAndAccommodationCost, formatIndianCurrency),
  otherCost: formatNumericTotalField(projectTotals.otherCostHasValue, projectTotals.otherCost, formatIndianCurrency),
  consultantCost: formatNumericTotalField(projectTotals.consultantCostHasValue, projectTotals.consultantCost, formatIndianCurrency),
  softwareHardwareCost: formatNumericTotalField(projectTotals.softwareHardwareCostHasValue, projectTotals.softwareHardwareCost, formatIndianCurrency),
  projectExpense: formatNumericTotalField(projectTotals.projectExpenseHasValue, projectTotals.projectExpense, formatIndianCurrency),
  overallTotalCost: formatNumericTotalField(projectTotals.overallTotalCostHasValue, projectTotals.overallTotalCost, formatIndianCurrency),
  rowConfiguration: {
    template: [
      { field: 'projectName', fontWeight: '700' },
      ...BOLD_NUMERIC_FIELDS.map((field) => ({ field, fontWeight: '700' })),
      { field: 'expectedDistribution', fontWeight: '700' },
    ],
  },
  _isProjectTotalRow: true,
});

// Groups the raw (unformatted) rows by projectId and computes each project's
// subtotal, using the same per-field sum-vs-take-once logic as the backend's
// buildGrandTotals (business.js) so these subtotals match the grand total.
const buildProjectTotalsByRow = (rawRows) => {
  const groupsByProjectId = new Map();

  rawRows.forEach((row, index) => {
    if (!row) return;
    const projectId = row.projectId;
    if (!groupsByProjectId.has(projectId)) {
      groupsByProjectId.set(projectId, {
        projectName: row.projectName,
        firstIndex: index,
        totals: {
          loggedEffort: 0,
          loggedEffortHasValue: false,
          unloggedEffort: 0,
          unloggedEffortHasValue: false,
          totalEffort: 0,
          totalEffortHasValue: false,
          expectedEffort: 0,
          expectedEffortHasValue: false,
          salaryDistribution: 0,
          salaryDistributionHasValue: false,
          overallTotalCost: toSafeNumber(row.overallTotalCost),
          overallTotalCostHasValue: isPresentValue(row.overallTotalCost),
          travelAndAccommodationCost: toSafeNumber(row.travelAndAccommodationCost),
          travelAndAccommodationCostHasValue: isPresentValue(row.travelAndAccommodationCost),
          otherCost: toSafeNumber(row.otherCost),
          otherCostHasValue: isPresentValue(row.otherCost),
          consultantCost: toSafeNumber(row.consultantCost),
          consultantCostHasValue: isPresentValue(row.consultantCost),
          softwareHardwareCost: toSafeNumber(row.softwareHardwareCost),
          softwareHardwareCostHasValue: isPresentValue(row.softwareHardwareCost),
          projectExpense: toSafeNumber(row.projectExpense),
          projectExpenseHasValue: isPresentValue(row.projectExpense),
          invoiceAmount: toSafeNumber(row.invoiceAmount),
          invoiceAmountHasValue: isPresentValue(row.invoiceAmount),
        },
      });
    }

    const group = groupsByProjectId.get(projectId);
    SUMMED_NUMERIC_FIELDS.forEach((field) => {
      if (isPresentValue(row[field])) {
        group.totals[field] += toSafeNumber(row[field]);
        group.totals[`${field}HasValue`] = true;
      }
    });
  });

  const firstIndexToGroup = new Map();
  groupsByProjectId.forEach((group) => {
    firstIndexToGroup.set(group.firstIndex, group);
  });
  return firstIndexToGroup;
};

const hasFilterValue = (item) => {
  if (!item || item?.type !== 'filter') return false;
  if (Array.isArray(item?.selectedValues) && item.selectedValues.length > 0)
    return true;
  if (item?.selectedValue ?? item?.inputValue ?? item?.value)
    return true;
  if (Array.isArray(item?.range) && item.range.some(Boolean))
    return true;
  return false;
};

const getAppliedFiltersFromConfiguration = (tableConfiguration = []) =>
  tableConfiguration
    .flatMap((column) => column?.menuItems || [])
    .flatMap((menuItem) => menuItem?.items || [])
    .filter(hasFilterValue);

// ─── EmptyState (same pattern as OfferingWiseReport) ────────────────────────

const EmptyState = ({ message }) => {
  const animationContainer = useRef(null);

  useEffect(() => {
    if (animationContainer.current) {
      const anim = lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: emptyStateAnimationData,
      });
      return () => anim.destroy();
    }
    return undefined;
  }, []);

  return (
    <div className="ms-empty-state-outer">
      <div className="ms-empty-state">
        <div className="ms-empty-state-animation-card">
          <div ref={animationContainer} className="ms-empty-state-animation" />
          <p className="ms-empty-state-text">{message}</p>
        </div>
      </div>
    </div>
  );
};

EmptyState.propTypes = { message: PropTypes.string.isRequired };

// ─── Note Banner (injected via portal above the table) ───────────────────────

const ProjectCostNoteWidget = () => (
  <div className="project-cost-report-note">
    <span className="project-cost-report-note__text">
      <strong>Note:</strong> The amounts in the table are in INR format and the
      efforts are in hours
    </span>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const ProjectCostReport = () => {
  const { userProfile } = useAuth();
  const authToken = userProfile?.access_token || getStoredAuthToken();

  const userInfo = useMemo(() => ({
    id: userProfile?.employeeId,
    emailId: userProfile?.email || '',
    email: userProfile?.email || '',
    emp_id: userProfile?.employeeId || userProfile?.emp_id || '',
    empId: userProfile?.employeeId || userProfile?.emp_id || '',
    employeeId: userProfile?.employeeId || '',
    employee_id: userProfile?.employeeId || '',
    permissions: userProfile?.permissions || {},
    roleDetails: Array.isArray(userProfile?.roleDetails) ? userProfile.roleDetails : [],
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [
    userProfile?.employeeId,
    userProfile?.email,
    userProfile?.emp_id,
    userProfile?.permissions,
    userProfile?.roleDetails,
    userProfile?.siteConfiguration,
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  const selectedMonthRef = useRef({ month: null, fromMonthYear: null, toMonthYear: null });
  const containerRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.add('project-cost-report-active');
    document.body.classList.add('project-cost-report-active');
    return () => {
      document.documentElement.classList.remove('project-cost-report-active');
      document.body.classList.remove('project-cost-report-active');
    };
  }, []);

  const setSelectedMonth = useCallback((monthValue) => {
    if (!monthValue) {
      selectedMonthRef.current = { month: null, fromMonthYear: null, toMonthYear: null };
      return;
    }
    const fromMonthYear = toApiMonthRangeValue(monthValue);
    const toMonthYear = toApiMonthRangeValue(monthValue, { useEndOfMonth: true });
    selectedMonthRef.current = {
      month: monthValue,
      fromMonthYear,
      toMonthYear,
    };
  }, []);

  // Reset the month ref to null when the toolbar reset or filter reset button is clicked
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleResetClick = (event) => {
      const isToolbarReset = event.target.closest('button')?.querySelector('.pi-refresh');
      const isFilterReset = event.target.closest('.tu-filter-footer-link') || event.target.closest('.tu-filter-footer-reset');
      if (isToolbarReset || isFilterReset) {
        setSelectedMonth(null);
        setHasAppliedFilters(false);
      }
    };

    container.addEventListener('click', handleResetClick, true);
    return () => container.removeEventListener('click', handleResetClick, true);
  }, [setSelectedMonth]);

  useEffect(() => {
    const handleCalendarApplyFlow = (event) => {
      const footerApply = event.target.closest('.tu-filter-footer-apply');
      if (footerApply) {
        const monthInput = document.querySelector('.p-calendar input');
        if (monthInput?.value) {
          setSelectedMonth(monthInput.value);
        }
        return;
      }

      const footerReset = event.target.closest('.tu-filter-footer-reset');
      if (footerReset) {
        setSelectedMonth(null);
        return;
      }

      const calendarButton = event.target.closest('.p-tieredmenu .filter-button, .p-tieredmenu .p-button, .custom-calendar-panel .p-button');
      if (!calendarButton) return;

      const buttonLabel = calendarButton.textContent?.trim();
      if (buttonLabel === 'Apply') {
        const monthInput = calendarButton.closest('.p-tieredmenu, div')?.querySelector('.p-calendar input')
          || document.querySelector('.p-calendar input');
        if (monthInput?.value) {
          setSelectedMonth(monthInput.value);
        }
      } else if (buttonLabel === 'Reset') {
        setSelectedMonth(null);
      }
    };

    document.addEventListener('click', handleCalendarApplyFlow, true);
    return () => document.removeEventListener('click', handleCalendarApplyFlow, true);
  }, [setSelectedMonth]);

  useEffect(() => {
    // Auto-open calendar popup when Month & Year filter is rendered
    const observer = new MutationObserver(() => {
      const menu = document.querySelector('.responsive-tiered-menu1, .p-tieredmenu, .custom-calendar-panel');
      if (!menu || menu.dataset.autoOpened === 'true') return;

      const monthInput = menu.querySelector('.p-calendar input');
      if (monthInput) {
        menu.dataset.autoOpened = 'true';
        setTimeout(() => {
          monthInput.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
          monthInput.focus();
          monthInput.click();
        }, 0);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Fetch filter-options on mount so the global filter panel populates immediately
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setIsLoading(true);
        await UseAxios({
          url: `${PROJECT_COST_REPORT}/filters`,
          method: 'post',
          headers: { 'content-type': 'application/json' },
          authRequired: true,
          service: serviceName.PROJECT_MS,
          payload: { userInfo },
        });
      } catch {
        toast.error('Error fetching filter options');
      } finally {
        setIsLoading(false);
        setFiltersLoaded(true);
      }
    };
    fetchFilters();
  }, [userInfo]);

  const handleProjectCostApiResponse = useCallback((response) => {
    setIsLoading(false);
    if (!response || typeof response !== 'object') return response;

    // A successful month-filtered response is authoritative even when the
    // utility's transient filter state omits the selected month.
    if (Array.isArray(response.data?.tableData) && response.data.tableData.length > 0) {
      setHasAppliedFilters(true);
    }

    // Numeric fields that need right-alignment
    const NUMERIC_FIELDS = new Set([
      'invoiceAmount', 'loggedEffort', 'unloggedEffort', 'totalEffort',
      'expectedEffort', 'expectedDistribution', 'salaryDistribution',
      'travelAndAccommodationCost', 'otherCost', 'consultantCost',
      'softwareHardwareCost', 'projectExpense', 'overallTotalCost',
    ]);

    // Inject the info icon, manager column formatting, and right-align all numeric/currency columns
    if (Array.isArray(response.data?.tableConfiguration)) {
      const hasCombinedCol = response.data.tableConfiguration.some(
        (col) => col.field === 'deliveryManagerSrManager',
      );

      let configs = response.data.tableConfiguration;
      if (!hasCombinedCol) {
        let inserted = false;
        configs = configs.reduce((acc, col) => {
          if (col.field === 'deliveryManager' || col.field === 'srManagerProjectManager') {
            if (!inserted) {
              inserted = true;
              acc.push({
                ...col,
                field: 'deliveryManagerSrManager',
                header: 'Delivery Manager / Sr. Manager / Project Manager',
                width: 300,
              });
            }
          } else {
            acc.push(col);
          }
          return acc;
        }, []);
      }

      response.data.tableConfiguration = configs.map((col) => {
        let updated = { ...col };

        // Right-align numeric and currency columns
        if (NUMERIC_FIELDS.has(col.field) || col.type === 'number' || col.type === 'currency') {
          updated = {
            ...updated,
            align: 'right',
            headerAlign: 'right',
            style: { ...(updated.style || {}), textAlign: 'right' },
            headerStyle: { ...(updated.headerStyle || {}), textAlign: 'right' },
          };
        }

        if (col.field === 'projectExpense') {
          return {
            ...updated,
            header: (
              <span>
                Project Expense (INR){' '}
                <span
                  title="Project Expense = Travel and Accommodation Cost + Other Cost + Consultant Cost + Software/Hardware Cost"
                  style={{ cursor: 'pointer', color: '#6366f1', marginLeft: '4px' }}
                >
                  <i className="pi pi-info-circle" />
                </span>
              </span>
            ),
          };
        }
        if (
          col.field === 'deliveryManagerSrManager'
          || col.field === 'deliveryManager'
          || col.field === 'srManagerProjectManager'
        ) {
          const colWidth = '300px';
          return {
            ...updated,
            header: 'Delivery Manager / Sr. Manager / Project Manager',
            width: colWidth,
            className: 'col-manager-summary',
            bodyClassName: 'col-manager-summary',
            headerClassName: 'col-manager-summary',
            style: {
              ...(updated.style || {}),
              width: colWidth,
              maxWidth: colWidth,
              minWidth: colWidth,
            },
            headerStyle: {
              ...(updated.headerStyle || {}),
              width: colWidth,
              maxWidth: colWidth,
              minWidth: colWidth,
            },
            body: (rowNode) => {
              const rowData = rowNode?.data || rowNode || {};
              const fullVal = getManagerSummary(rowData);
              return (
                <div
                  title={fullVal}
                  className="manager-cell-inner"
                  style={{
                    maxWidth: '100%',
                    width: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    display: 'block',
                  }}
                >
                  {fullVal}
                </div>
              );
            },
          };
        }
        return updated;
      });
    }

    const tableData = response.data?.tableData || [];
    const grandTotals = response.data?.grandTotals;

    // The dynamic-table library renders each cell's raw field value directly
    // (it does not invoke a per-column `body` render override), so numeric
    // values must be pre-formatted into comma-separated display strings here,
    // matching emp_salary_project_expense_report.php's number formatting.
    const applyCommaFormatting = (row) => {
      if (!row || row._isFooterRow) return row;
      const formattedRow = { ...row };
      WESTERN_COMMA_FIELDS.forEach((field) => {
        if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
          formattedRow[field] = formatWesternNumber(row[field]);
        }
      });
      INDIAN_COMMA_FIELDS.forEach((field) => {
        if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
          formattedRow[field] = formatIndianCurrency(row[field]);
        }
      });
      ZERO_DISPLAY_COST_FIELDS.forEach((field) => {
        formattedRow[field] = formatIndianCurrency(0);
      });
      return formattedRow;
    };

    // Apply color styling via rowConfiguration template settings for each normal row
    const styledTableData = tableData.map((row) => {
      const commaFormattedRow = applyCommaFormatting(row);
      if (!commaFormattedRow || commaFormattedRow._isFooterRow) return commaFormattedRow;
      const color = commaFormattedRow.statusColor || commaFormattedRow.status_color || '';
      if (!color) return commaFormattedRow;

      const existingTemplate = Array.isArray(commaFormattedRow.rowConfiguration?.template)
        ? commaFormattedRow.rowConfiguration.template
        : [];

      return {
        ...commaFormattedRow,
        rowConfiguration: {
          ...(commaFormattedRow.rowConfiguration || {}),
          template: [
            ...existingTemplate.filter((entry) => entry.field !== 'projectStatus'),
            { field: 'projectStatus', textColor: color, fontWeight: '600' },
          ],
        },
      };
    });

    // Insert a bold, comma-formatted project-wise total row at the top of each
    // project section, followed by the employee rows of that project.
    const firstIndexToProjectGroup = buildProjectTotalsByRow(tableData);
    const tableDataWithProjectTotals = [];
    styledTableData.forEach((row, index) => {
      const projectGroup = firstIndexToProjectGroup.get(index);
      if (projectGroup) {
        tableDataWithProjectTotals.push(
          buildProjectTotalRow(projectGroup.totals, `${projectGroup.projectName} - Total`),
        );
      }
      tableDataWithProjectTotals.push(row);
    });

    if (response?.data?.tableConfiguration) {
      response.data.tableConfiguration = response.data.tableConfiguration.map((col) => {
        return {
          ...col,
          sortFunction: (event) => {
            const { data, field, order } = event;
            data.sort((rowA, rowB) => {
              if (rowA._isFooterRow) return 1;
              if (rowB._isFooterRow) return -1;

              const valA = rowA[field];
              const valB = rowB[field];

              if (valA == null && valB != null) return -1 * order;
              if (valA != null && valB == null) return Number(order);
              if (valA == null && valB == null) return 0;

              if (typeof valA === 'string' && typeof valB === 'string') {
                return valA.localeCompare(valB, undefined, { numeric: true }) * order;
              }

              return (valA < valB ? -1 : valA > valB ? 1 : 0) * order;
            });
            return data;
          },
        };
      });
    }

    if (grandTotals && styledTableData.length > 0) {
      const footerRow = {
        projectName: 'Total',
        clientName: '',
        projectRegion: '',
        projectCountry: '',
        projectVertical: '',
        projectType: '',
        projectStatus: '',
        billingEntity: '',
        deliveryManagerSrManager: '',
        salesSpoc: '',
        actualStartDate: '',
        replannedEndDate: '',
        invoiceAmount: formatIndianCurrency(toSafeNumber(grandTotals.invoiceAmount)),
        zohoClientId: '',
        zohoClientName: '',
        employeeId: '',
        employeeName: '',
        loggedEffort: formatWesternNumber(toSafeNumber(grandTotals.loggedEffort)),
        unloggedEffort: formatWesternNumber(toSafeNumber(grandTotals.unloggedEffort)),
        totalEffort: formatWesternNumber(toSafeNumber(grandTotals.totalEffort)),
        expectedEffort: formatWesternNumber(toSafeNumber(grandTotals.expectedEffort)),
        expectedDistribution: toSafeNumber(grandTotals.expectedDistribution),
        salaryDistribution: formatIndianCurrency(toSafeNumber(grandTotals.salaryDistribution)),
        travelAndAccommodationCost: formatIndianCurrency(toSafeNumber(grandTotals.travelAndAccommodationCost)),
        otherCost: formatIndianCurrency(toSafeNumber(grandTotals.otherCost)),
        consultantCost: formatIndianCurrency(toSafeNumber(grandTotals.consultantCost)),
        softwareHardwareCost: formatIndianCurrency(toSafeNumber(grandTotals.softwareHardwareCost)),
        projectExpense: formatIndianCurrency(toSafeNumber(grandTotals.projectExpense)),
        overallTotalCost: formatIndianCurrency(toSafeNumber(grandTotals.overallTotalCost)),
        rowConfiguration: {
          isFrozen: true,
          template: [
            { field: 'projectName', fontWeight: '700' },
            { field: 'invoiceAmount', fontWeight: '700' },
            { field: 'loggedEffort', fontWeight: '700' },
            { field: 'unloggedEffort', fontWeight: '700' },
            { field: 'totalEffort', fontWeight: '700' },
            { field: 'expectedEffort', fontWeight: '700' },
            { field: 'expectedDistribution', fontWeight: '700' },
            { field: 'salaryDistribution', fontWeight: '700' },
            { field: 'travelAndAccommodationCost', fontWeight: '700' },
            { field: 'otherCost', fontWeight: '700' },
            { field: 'consultantCost', fontWeight: '700' },
            { field: 'softwareHardwareCost', fontWeight: '700' },
            { field: 'projectExpense', fontWeight: '700' },
            { field: 'overallTotalCost', fontWeight: '700' },
          ],
        },
        _isFooterRow: true,
      };

      response.data.tableData = [...tableDataWithProjectTotals, footerRow];
    }
    return response;
  }, []);

  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        enableApiSorting: true,
        baseURL: projectCostReportBaseUrl,
        showToolBar: true,
        showPagination: false,
        showTieredMenu: true,
        exportHeaderUnmerge: true,
        reportTitle: 'Project Cost Report',
        userInfo,
        headers: { token: authToken },
        additionalPayloadData: {
          get fromMonthYear() {
            return selectedMonthRef.current.fromMonthYear;
          },
          get toMonthYear() {
            return selectedMonthRef.current.toMonthYear;
          },
          get startDate() {
            return selectedMonthRef.current.fromMonthYear;
          },
          get endDate() {
            return selectedMonthRef.current.toMonthYear;
          },
          get month() {
            return selectedMonthRef.current.month;
          },
          get selectedDate() {
            return selectedMonthRef.current.month;
          },
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: getExportFileName('Project_Cost_Report'),
            sheetName: 'Project Cost Report',
            reportTitle: () => 'Project Cost Report',
            transformResponse: (exportPayload) => transformProjectCostExport(exportPayload),
          },
        },
        handler: {
          handleFilteredApiResponse: handleProjectCostApiResponse,
          onFilterUpdate: ({ action, filters }) => {
            const safeFilters = Array.isArray(filters) ? filters : [];
            if (action === 'apply') {
              const monthFilter = safeFilters.find(
                (data) => data.field === 'dateRange' || data.field === 'month' || data.field === 'selectedDate' || data.template === 'monthCalendar',
              );
              const selectedVal = monthFilter?.selectedValue
                || monthFilter?.value
                || (Array.isArray(monthFilter?.range) && monthFilter.range.find(Boolean))
                || selectedMonthRef.current.fromMonthYear;
              if (!selectedVal) {
                toast.error('Please select month before applying.');
                return;
              }
              if (monthFilter?.selectedValue || monthFilter?.value) {
                setSelectedMonth(monthFilter?.selectedValue || monthFilter?.value);
              }
              setIsLoading(true);
            } else if (action === 'reset') {
              setIsLoading(false);
              setSelectedMonth(null);
              setHasAppliedFilters(false);
            }
          },
          setTableStateHandler: (getTableState) => {
            const tableState = getTableState?.();
            const configFilters = getAppliedFiltersFromConfiguration(
              tableState?.tableConfiguration || [],
            );
            const stateFilters = Array.isArray(tableState?.filters)
              ? tableState.filters.filter(hasFilterValue)
              : [];
            const appliedFilters = [...stateFilters, ...configFilters];

            // Report Month is mandatory
            const hasReportMonth = appliedFilters.some(
              (filterItem) =>
                (filterItem.field === 'dateRange' || filterItem.field === 'month' || filterItem.field === 'selectedDate' || filterItem.template === 'monthCalendar')
                && Boolean(
                  filterItem.selectedValue
                  || filterItem.value
                  || (Array.isArray(filterItem.range) && filterItem.range.some(Boolean))
                  || selectedMonthRef.current.fromMonthYear,
                ),
            );
            setHasAppliedFilters((prev) =>
              prev === hasReportMonth ? prev : hasReportMonth,
            );
          },
        },
        toolBarSettings: {
          showAddNew: false,
          showDownloadExcel: true,
          showAppliedFilters: true,
          showColumnCustomize: false,
          showColumnFilterMenu: true,
          showHelp: false,
          showSearch: false,
          showToolBarHeading: true,
          toolBarHeading: 'Project Cost Report',
          additionalElements: <ProjectCostNoteWidget />,
          downloadExcelFileName: getExportFileName('Project_Cost_Report'),
          downloadExcelSheetName: 'Project Cost Report',
          showSaveView: false,
          showReset: false,
          showExpandCollapse: false,
          showFilterOption: true,
          showFilters: true,
        },
      },
      tableSettings: {
        size: 'small',
        pageSize: 10,
        tableScrollHeight: 'calc(100vh - 260px)',
        loaderSkeleton: { rows: 10, bodyCells: 2, columns: 5 },
        paginationConfig: { page: 1, limit: 10, rowsPerPageOptions: [10] },
        showExpandCollapse: false,
      },
    }),
    [userInfo, authToken, handleProjectCostApiResponse],
  );

  return (
    <div
      ref={containerRef}
      className={`project-cost-report-outer${hasAppliedFilters ? '' : ' awaiting-filters'}`}
    >
      {isLoading && (
        <div className="loader-container">
          <img
            src={PuzzleLoader}
            alt="Loading..."
            style={{ width: '100px', height: 'auto' }}
          />
        </div>
      )}

      <div className="project-cost-report-table-wrapper">
        {filtersLoaded && (
          <TableUtility configuration={configuration} showFilterOption />
        )}

        {!hasAppliedFilters && (
          <EmptyState message="Please select the Report Month filter to view the results" />
        )}

      </div>
    </div>
  );
};

export default ProjectCostReport;
